import { Location } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { CheckEditUserPermissionsPipe } from "src/app/shared/pipes/check-edit-user-permissions/check-edit-user-permissions.pipe";
import { allowDeleteEntity, allowUpdateEntity } from "src/app/shared/utils/helpers/permission.helper";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DataTableComponent } from "../../../../shared/components/data-table/data-table.component";
import {
  DataTable,
  DataTableParamsWhere,
  GetDataEvent,
} from "../../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../../shared/components/delete-modal/delete-modal.component";
import {
  DynamicFormComponent,
  DynamicFormEditBtn,
} from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { GenericModalComponent } from "../../../../shared/components/generic-modal/generic-modal.component";
import { GenericModalData } from "../../../../shared/components/generic-modal/generic-modal.type";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { AnyObject } from "../../../../shared/types/app.type";
import { UserInfo, UserRights, UserTypes } from "../../../../shared/types/user-info";
import { getUpdatedValues, isRequiredField } from "../../../../shared/utils/helpers/common.helper";
import { CompanyTableService } from "../../companies/company-table.service";
import { CompanyService } from "../../companies/company.service";
import { Company } from "../../companies/company.type";
import { PartnerTableService } from "../../partners/partner-table.service";
import { PartnerService } from "../../partners/partner.service";
import { Partner } from "../../partners/partner.type";
import { CreateUserService } from "../create-user/create-user.service";
import { UserService } from "../user.service";
import { User } from "../user.type";
import { finalize } from "rxjs/operators";

@Component({
  selector: "app-manage-user",
  templateUrl: "./manage-user.component.html",
  styleUrls: ["./manage-user.component.scss"],
  providers: [CreateUserService],
})
export class ManageUserComponent implements OnInit {
  // Dynamic forms
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;
  @ViewChild("formEditUserInformation") formEditUserInformation: DynamicFormComponent;
  @ViewChild("formEditUserPermissions") formEditUserPermissions: DynamicFormComponent;
  @ViewChild("formEditUserNotification") formEditUserNotification: DynamicFormComponent;
  companiesTableConfig: DataTable<Company>;
  partnersTableConfig: DataTable<Partner>;
  manageUserForm: FormGroup;
  loading = false;
  submitted = false;
  userToEdit: User;
  userCompanyToEdit: Company;
  isEdit = false;
  isEditUserInformation = false;
  isEditUserPermissions = false;
  isEditUserNotification = false;
  allowLinkCompanies = false;
  allowLinkPartners = false;
  companies: Company[];
  partners: Partner[];
  currentUser: UserInfo;
  showCompanyInput = false;
  showPartnerInput = false;
  loading$ = this.loader.loading$;
  questions: QuestionBase<string | boolean>[];
  questionsEditInformation: QuestionBase<string | boolean>[];
  questionsEditPermission: QuestionBase<string | boolean>[];
  questionsEditNotification: QuestionBase<string | boolean>[];
  showIsFutureExpirationNotification = true;
  public isRequiredField = isRequiredField;
  company: Company;

  editInformationBtn: DynamicFormEditBtn = {
    label: "Edit",
    show: true,
    onClick: () => this.toggleManageUserInformation(),
  };
  editPermissionBtn: DynamicFormEditBtn = {
    label: "Edit",
    show: true,
    onClick: () => this.toggleManageUserPermission(),
  };
  editNotificationBtn: DynamicFormEditBtn = {
    label: "Edit",
    show: true,
    onClick: () => this.toggleManageUserNotification(),
  };

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<DeleteModalComponent>,
    public location: Location,
    public companiesTableService: CompanyTableService,
    public partnersTableService: PartnerTableService,
    private loader: LoadingService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
    private questionsService: CreateUserService,
    private companyService: CompanyService,
    private partnerService: PartnerService,
    private companyTableService: CompanyTableService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }
  // #region Lifecycle
  async ngOnInit(): Promise<void> {
    // if it's trying to edit their own user, then it redirects to their profile
    const userId = this.route.snapshot.paramMap.get("id");
    if (userId === this.currentUser.id) {
      this.router.navigate(["/profile/"], { replaceUrl: true });
    } else {
      try {
        await this.setUserToEdit(userId);
        this.editInformationBtn.hiddenPipe = {
          name: CheckEditUserPermissionsPipe,
          args: { currentUser: this.currentUser, userToEdit: this.userToEdit },
        };
        this.editPermissionBtn.hiddenPipe = {
          name: CheckEditUserPermissionsPipe,
          args: { currentUser: this.currentUser, userToEdit: this.userToEdit },
        };
        this.editNotificationBtn.hiddenPipe = {
          name: CheckEditUserPermissionsPipe,
          args: { currentUser: this.currentUser, userToEdit: this.userToEdit },
        };
        this.allowLinkCompanies =
          (!this.currentUser.isCompanyAdmin() && this.userToEdit.type === UserTypes.Company) ||
          (this.userToEdit.type === UserTypes.Partner &&
            this.userToEdit.right === UserRights.Manager &&
            this.userToEdit.isSalesRep);
        this.setAllowLinkPartners(this.userToEdit);

        if (this.userToEdit.companyId && !this.currentUser.isCompany()) {
          this.userCompanyToEdit = await this.getCompany(this.userToEdit.companyId);
        }
        this.buildForms(this.userToEdit);
      } catch (error) {
        this.router.navigate(["/users"]);
        this.notificationService.error(error || "Couldn't find User");
      }
    }

    this.setTabs();
  }
  // #endregion

  // #region Component core methods
  goBack(): void {
    this.location.back();
  }
  titleLabel(): string {
    return this.checkEditPermissions() ? "Edit User:" : "User:";
  }

  async setUserToEdit(userId: string): Promise<void> {
    this.userToEdit = await this.userService.getUser(userId, { relations: { partners: true } }).toPromise();
  }

  async getCompany(companyId: string): Promise<Company> {
    return this.companyService.getCompany(companyId).toPromise();
  }
  onChange(event: AnyObject): void {
    const field: string = event.source?.id;

    // show or hide partner and company fields
    // TODO: when replacing the question, the callback of the replaced question is executed. We need to prevent it.

    if (field !== "type" && field !== "right") {
      return;
    }

    const type = event.value;

    if (type === UserTypes.Global) {
      // The partner and company fields must not be present
      this.formEditUserPermissions.removeQuestion("partnerId");
      this.formEditUserPermissions.removeQuestion("companyId");
    } else if (type === UserTypes.Partner) {
      // The company field must not be present
      this.formEditUserPermissions.removeQuestion("companyId");
      // The partner field must be present
      if (!this.currentUser.isPartnerAdminOrManager()) {
        this.formEditUserPermissions.replaceOrAppendQuestion(this.questionsService.createPartnerDropdown());
      }
      this.formEditUserPermissions.removeQuestion("isCWRep");
    } else if (type === UserTypes.Company) {
      // The partner and company fields must be present
      if (!this.currentUser.isPartnerAdminOrManager()) {
        this.formEditUserPermissions.replaceOrAppendQuestion(this.questionsService.createPartnerDropdown());
      }
      // The company field must be present
      this.formEditUserPermissions.replaceOrAppendQuestion(
        this.questionsService.createCompanyDropdown({
          ...(this.currentUser.partnerId && { partnerId: this.currentUser.partnerId }),
        }),
      );
      this.formEditUserPermissions.removeQuestion("isCWRep");
      this.formEditUserPermissions.removeQuestion("isSalesRep");
    }
    if (
      this.formEditUserPermissions.form.value.type === UserTypes.Partner &&
      this.formEditUserPermissions.form.value.right === UserRights.Manager
    ) {
      this.formEditUserPermissions.replaceOrAppendQuestion(
        this.questionsService.createIsRepresentativeDropdown({
          key: "isSalesRep",
          label: "Is Sales Rep",
          disabled: !this.currentUser.isGlobalAdmin(),
        }),
      );
    } else {
      this.formEditUserPermissions.removeQuestion("isSalesRep");
    }
    if (this.currentUser.isGlobal()) {
      if (
        this.formEditUserPermissions.form.value.type === UserTypes.Global &&
        this.formEditUserPermissions.form.value.right === UserRights.Manager
      ) {
        this.formEditUserPermissions.replaceOrAppendQuestion(
          this.questionsService.createIsRepresentativeDropdown({
            key: "isCWRep",
            label: "Is CW Rep",
            disabled: !this.currentUser.isGlobalAdmin(),
          }),
        );
      } else {
        this.formEditUserPermissions.removeQuestion("isCWRep");
      }
    }
  }

  buildForms(userToEdit: User): void {
    this.loader.show();
    this.buildFormEditUserInformation(userToEdit);
    this.buildFormEditUserPermissions(userToEdit);
    const isFutureExpirationNotificationEnabled = this.showIsFutureExpirationNotificationEnabled(userToEdit);
    if (isFutureExpirationNotificationEnabled) {
      this.buildFormEditUserNotification(userToEdit);
    }
  }

  buildFormEditUserInformation(user: User): void {
    this.questionsService
      .generateQuestionsEditUserInformation({
        disableEmail: true,
        userToEdit: user,
        currentUser: this.currentUser,
      })
      .subscribe((qs) => {
        this.questionsEditInformation = qs;

        if (this.formEditUserInformation) {
          this.formEditUserInformation.resetWithValues(this.questionsEditInformation);
          this.toggleManageUserInformation();
        }
      })
      .add(() => this.loader.hide());
  }

  buildFormEditUserPermissions(user: User): void {
    this.questionsService
      .generateQuestionsEditUserPermission({
        partner: user.partner || user.company?.partner,
        company: user.company,
        currentUser: this.currentUser,
        hideCompany: user.type !== UserTypes.Company,
        hidePartner: user.type === UserTypes.Global || this.currentUser.isPartnerAdminOrManager(),
        skipPartner: user.type === UserTypes.Global || this.currentUser.isPartnerAdminOrManager(),
        skipCompany: user.type !== UserTypes.Company,
        disableEmail: true,
        user,
        isEdit: true,
        ...(this.currentUser.isPartnerAdminOrManager() && { partner: this.currentUser.partner }),
        isSalesRep: user.type === UserTypes.Partner && user.right === UserRights.Manager,
        isCWRep: user.type === UserTypes.Global && user.right === UserRights.Manager,
      })
      .subscribe((qs) => {
        this.questionsEditPermission = qs;

        if (this.formEditUserPermissions) {
          this.formEditUserPermissions.resetWithValues(this.questionsEditPermission);
        }
      })
      .add(() => this.loader.hide());
  }

  buildFormEditUserNotification(user: User): void {
    this.questionsService
      .generateQuestionsEditUserNotification({
        userToEdit: user,
      })
      .subscribe((qs) => {
        this.questionsEditNotification = qs;
        if (this.formEditUserNotification) {
          this.formEditUserNotification.resetWithValues(this.questionsEditNotification);
        }
      })
      .add(() => this.loader.hide());
  }

  toggleAllowLinkCompanies(user: User): void {
    if (user.isSalesRep) {
      this.allowLinkCompanies = true;
    } else {
      this.allowLinkCompanies = false;
    }
  }
  toggleManageUser(): void {
    this.isEdit = !this.isEdit;
  }

  toggleAllowLinkPartners(user: User): void {
    const isGlobalAdmin = user.type === UserTypes.Global && user.right === UserRights.Admin;
    if (user.isCWRep || isGlobalAdmin) {
      this.allowLinkPartners = true;
    } else {
      this.allowLinkPartners = false;
    }
  }

  setAllowLinkPartners(user: User): void {
    const isGlobalAdmin = user.type === UserTypes.Global && user.right === UserRights.Admin;
    this.allowLinkPartners =
      (user.type === UserTypes.Global && user.right === UserRights.Manager && user.isCWRep) || isGlobalAdmin;
  }

  checkEditPermissions(): boolean {
    if (!this.currentUser) {
      return;
    }
    if (
      (this.currentUser.isGlobal() && this.currentUser.hasRight(["admin", UserRights.Manager])) ||
      this.currentUser.isPartnerAdmin() ||
      (this.currentUser.isCompanyAdmin() && this.currentUser.companyId === this.userToEdit.companyId) ||
      (this.currentUser.isPartner() && this.currentUser.hasRight([UserRights.Manager]) && !!this.userToEdit.companyId)
    )
      return allowUpdateEntity(this.userToEdit, this.currentUser);

    return false;
  }

  showIsFutureExpirationNotificationEnabled(userToEdit: User): boolean {
    const currentUserIsAdminOrManager =
      this.currentUser.isGlobalAdminOrManager() || this.currentUser.isPartnerAdminOrManager();

    const userToEditIsPartnerAdminOrManager =
      userToEdit.type === UserTypes.Partner &&
      (userToEdit.right === UserRights.Admin || userToEdit.right === UserRights.Manager);

    const isGlobalAdmin = userToEdit.type === UserTypes.Global && userToEdit.right === UserRights.Admin;

    if (
      (currentUserIsAdminOrManager && userToEditIsPartnerAdminOrManager) ||
      (this.currentUser.isGlobalAdmin() && isGlobalAdmin)
    ) {
      return true;
    } else {
      return false;
    }
  }

  removeUndesiredFields(data: User): void {
    if (!data.companyId) {
      delete data.companyId;
    }
    if (!data.partnerId) {
      delete data.partnerId;
    }
    if (data.companyId) {
      delete data.partnerId;
    }
  }

  deleteUserModal(): void {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "user",
        data: this.userToEdit,
        url: this.userService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.router.navigate(["/users"]);
      }
    });
  }

  // #region Update user

  updateUserInformation(): void {
    const data = getUpdatedValues(this.formEditUserInformation.form, this.userToEdit);
    if (data) {
      this.loader.show();
      this.userService
        .updateUserInformation(this.userToEdit.id, data)
        .pipe(finalize(() => this.dynamicForm.enable()))
        .subscribe(
          async (user: User) => {
            this.notificationService.success("User updated");
            await this.setUserToEdit(user.id);
            this.buildFormEditUserInformation(this.userToEdit);
            this.loader.hide();
          },
          (err) => this.notificationService.error(err || "Could not update user"),
        )
        .add(() => this.loader.hide());
    }
  }

  updateUserPermissions(): void {
    const fieldsToInclude = [];
    if (this.currentUser.isGlobalAdmin()) {
      fieldsToInclude.push("partnerId", "companyId", "type");
    }

    let data = getUpdatedValues(this.formEditUserPermissions.form, this.userToEdit, fieldsToInclude);

    /** @workaround to sent type in data payload if current user is partner and tries to change a company from company user */
    if (this.currentUser.isPartner() && data["companyId"] && !data["type"]) {
      data = {
        ...data,
        type: "company",
      };
    }

    this.removeUndesiredFields(data as User);
    if (data) {
      this.loader.show();
      this.userService
        .updateUserPermissions(this.userToEdit.id, data)
        .pipe(finalize(() => this.dynamicForm.enable()))
        .subscribe(
          async (user: User) => {
            this.toggleAllowLinkCompanies(user);
            this.toggleAllowLinkPartners(user);
            this.toggleManageUserPermission();
            this.notificationService.success("User updated");
            await this.setUserToEdit(user.id);
            this.buildFormEditUserPermissions(this.userToEdit);

            // check if the user can access the notification form
            const isFutureExpirationNotificationEnabled = this.showIsFutureExpirationNotificationEnabled(
              this.userToEdit,
            );

            if (isFutureExpirationNotificationEnabled) {
              this.buildFormEditUserNotification(this.userToEdit);
              this.formEditUserNotification.showFormButtons(true);
            } else {
              this.formEditUserNotification.removeQuestion("isFutureExpirationNotificationEnabled");
              this.formEditUserNotification.showFormButtons(false);
            }

            this.loader.hide();
          },
          (err) => this.notificationService.error(err || "Could not update user"),
        )
        .add(() => this.loader.hide());
    }
  }

  updateUserNotification(): void {
    const data = getUpdatedValues(this.formEditUserNotification.form, this.userToEdit);
    if (data) {
      this.loader.show();
      this.userService
        .updateUserNotifications(this.userToEdit.id, data)
        .pipe(finalize(() => this.dynamicForm.enable()))
        .subscribe(
          async (user: User) => {
            this.toggleManageUserNotification();
            this.notificationService.success("User updated");
            await this.setUserToEdit(user.id);
            this.buildFormEditUserNotification(this.userToEdit);
            this.loader.hide();
          },
          (err) => this.notificationService.error(err || "Could not update user"),
        )
        .add(() => this.loader.hide());
    }
  }

  // #endregion

  // #region Handle edit buttons
  toggleManageUserInformation(): void {
    if (this.isEditUserInformation) {
      this.editInformationBtn.label = "Edit";
    } else {
      this.editInformationBtn.label = "Cancel";
    }
    this.isEditUserInformation = !this.isEditUserInformation;
  }

  toggleManageUserPermission(): void {
    if (this.isEditUserPermissions) {
      this.editPermissionBtn.label = "Edit";
    } else {
      this.editPermissionBtn.label = "Cancel";
    }
    this.isEditUserPermissions = !this.isEditUserPermissions;
  }

  toggleManageUserNotification(): void {
    if (this.isEditUserNotification) {
      this.editNotificationBtn.label = "Edit";
    } else {
      this.editNotificationBtn.label = "Cancel";
    }
    this.isEditUserNotification = !this.isEditUserNotification;
  }
  // #endregion

  // #endregion

  // #region Company & Partner Table getters

  getCompanies = (event: GetDataEvent<Company>): void => {
    const { onError, onResult, queryParams } = event;
    this.companyService
      .getCompanies({
        ...queryParams,
        where: {
          ...queryParams?.where,
          users: { id: this.userToEdit.id },
        },
        relations: { users: true },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  getPartners = (event: GetDataEvent<Partner>): void => {
    const { onError, onResult, queryParams } = event;
    this.partnerService
      .getPartners({
        ...queryParams,
        where: {
          ...queryParams?.where,
          users: { id: this.userToEdit.id },
        },
        relations: { users: true },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  /* Show/Hide delete user button  */
  canDeleteUser = () => this.checkEditPermissions() && allowDeleteEntity(this.userToEdit, this.currentUser);

  canUpdateInformation = () => this.checkEditPermissions();

  canUpdatePermissions = () =>
    this.checkEditPermissions() &&
    (this.currentUser.isGlobalAdmin() || this.currentUser.isPartnerAdmin() || this.currentUser.isCompanyAdmin());

  canUpdateNotifications = () =>
    this.checkEditPermissions() && (this.currentUser.isGlobalAdminOrManager() || this.currentUser.isPartnerAdmin());

  isDisabled(dynamicFormComponent: DynamicFormComponent): boolean {
    if (dynamicFormComponent?.form) {
      const form = dynamicFormComponent.form;
      const controls = form.controls;
      const hasInvalidDisabledFields = Object.keys(controls).some(
        (key) =>
          controls[key].disabled &&
          this.questionsEditPermission.find((qs) => qs.key === key).required &&
          controls[key]?.value === "",
      );
      return form.invalid || form.pristine || hasInvalidDisabledFields;
    }
  }

  private setTabs(): void {
    this.setCompanyTab();
    this.setPartnerTab();
  }
  // #endregion

  // #region Company Tab
  private setCompanyTab(): void {
    this.companiesTableConfig = this.companiesTableService.getCompaniesTableConfig({
      hidden: ["partner.name"],
      actionColumn: { delete: { hidden: true } },
      entities: { user: this.userToEdit },
    });
    this.companiesTableConfig = {
      ...this.companiesTableConfig,
      actionColumn: [
        ...this.companiesTableConfig.actionColumn,
        {
          icon: "link_off",
          text: "Unlink",
          function: this.unlinkCompany,
          hidden: !(this.currentUser.isGlobalAdmin() || this.currentUser.isPartnerAdmin()),
        },
      ],
      toolBar: {
        ...this.companiesTableConfig.toolBar,
        addItem: {
          function: this.linkCompanies,
          hidden: !(
            allowUpdateEntity(this.userToEdit, this.currentUser) &&
            (this.currentUser.isGlobalAdmin() || this.currentUser.isPartnerAdmin())
          ),
        },
      },
    };
  }

  private linkCompanies = async (table: DataTableComponent<Company>): Promise<void> => {
    const partnerId = this.userCompanyToEdit?.partnerId || this.userToEdit.partnerId;

    // Assign companies to the user (used in the popup table)
    const action = (companiesTable: DataTableComponent<Company>) => {
      if (companiesTable.config.selection.selectedRows.length) {
        this.loader.show();
        const body = { companies: companiesTable.config.selection.selectedRows.map((company) => company.id) };
        this.userService
          .linkCompanies(this.userToEdit.id, body)
          .subscribe(
            () => {
              this.notificationService.success("Companies linked");
              table.search();
            },
            (error) => this.notificationService.error(`Couldn't link companies to user: ${error}`),
          )
          .add(() => this.loader.hide());
      }
    };

    this.userToEdit = await this.userService
      .getUser(this.userToEdit.id, { relations: { companies: true } })
      .toPromise();

    this.dialog.open<GenericModalComponent, GenericModalData<{ config: DataTable<Company> }>>(GenericModalComponent, {
      width: "1400px",
      panelClass: "custom-dialog-container",
      data: {
        title: `Link companies to ${this.userToEdit.firstName}`,
        buttons: { hide: false, submit: { action } },
        component: {
          clazz: DataTableComponent,
          inputs: {
            config: this.companyTableService.getCompaniesTableConfig({
              hidden: ["partner.name"],
              actionColumn: { hidden: true },
              inlineEdit: { disabled: () => true },
              syncWithQueryParams: false,
              selection: { hidden: false },
            }),
          },
          events: [
            {
              eventName: "getData",
              handler: (_dynamicForm: any, event: GetDataEvent<Company>) => {
                return this.companyTableService.getCompaniesPost({
                  ...event,
                  queryParams: {
                    ...event.queryParams,
                    where: {
                      ...event.queryParams.where,
                      partnerId,
                      id: {
                        $nin: [...this.userToEdit.companies.map((p) => p.id), this.userToEdit.companyId].filter(
                          (companyId) => companyId,
                        ),
                      },
                    } as DataTableParamsWhere<Company>,
                  },
                });
              },
            },
          ],
        },
      },
    });
  };

  private unlinkCompany = (company: Company, table: DataTableComponent<Company>): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "Company",
        data: company,
        unlink: true,
        fullUrl: `${this.userService.basePath}/${this.userToEdit.id}/companies`,
        body: { companies: [company.id] },
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  // #endregion

  // #region Partner Tab
  private setPartnerTab(): void {
    this.partnersTableConfig = this.partnersTableService.getPartnersTableConfig({
      actionColumn: { delete: { hidden: true } },
      entities: { user: this.userToEdit },
    });
    this.partnersTableConfig = {
      ...this.partnersTableConfig,
      actionColumn: [
        ...this.partnersTableConfig.actionColumn,
        {
          icon: "link_off",
          text: "Unlink",
          function: this.unlinkPartnerModal,
          hidden: !this.currentUser.isGlobalAdmin(),
        },
      ],
      toolBar: {
        ...this.partnersTableConfig.toolBar,
        addItem: {
          function: this.showLinkPartnersToUserModal,
          hidden: !this.currentUser.isGlobalAdmin(),
        },
      },
    };
  }

  private unlinkPartnerModal = (partner: Partner, table: DataTableComponent<Partner>): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "Partner",
        data: partner,
        unlink: true,
        fullUrl: `${this.userService.basePath}/${this.userToEdit.id}/partners`,
        body: { partners: [partner.id] },
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  private showLinkPartnersToUserModal = async (table: DataTableComponent<Partner>): Promise<void> => {
    // Assign partners to the user (used in the popup table)
    const action = (partnersTable: DataTableComponent<Partner>) => {
      if (partnersTable.config.selection.selectedRows.length) {
        this.loader.show();
        const body = { partners: partnersTable.config.selection.selectedRows.map((partner) => partner.id) };
        this.userService
          .linkPartners(this.userToEdit.id, body)
          .subscribe(
            () => {
              this.notificationService.success("Partners linked");
              table.search();
            },
            (error) => this.notificationService.error(`Couldn't link partners to user: ${error}`),
          )
          .add(() => this.loader.hide());
      }
    };
    this.userToEdit = await this.userService.getUser(this.userToEdit.id, { relations: { partners: true } }).toPromise();

    this.dialog.open<GenericModalComponent, GenericModalData<{ config: DataTable<Partner> }>>(GenericModalComponent, {
      width: "1400px",
      panelClass: "custom-dialog-container",
      data: {
        title: `Link partners to ${this.userToEdit.firstName}`,
        buttons: { hide: false, submit: { action } },
        component: {
          clazz: DataTableComponent,
          inputs: {
            config: this.partnersTableService.getPartnersTableConfig({
              actionColumn: { hidden: true },
              inlineEdit: { disabled: () => true },
              syncWithQueryParams: false,
              selection: { hidden: false },
            }),
          },
          events: [
            {
              eventName: "getData",
              handler: (_dynamicForm: any, event: GetDataEvent<Partner>) => {
                return this.partnersTableService.getPartnersPost({
                  ...event,
                  queryParams: {
                    ...event.queryParams,
                    where: {
                      ...event.queryParams.where,
                      id: { $nin: this.userToEdit.partners.map((p) => p.id) },
                    } as DataTableParamsWhere<Partner>,
                  },
                });
              },
            },
          ],
        },
      },
    });
  };

  // #endregion
}
